import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { useShop } from "../../context/ShopContext";
import { api, img, inr } from "../../lib/api";

export function useQuote(extra = {}) {
  const { cart, coupon } = useShop();
  const [quote, setQuote] = useState(null);
  const key = JSON.stringify({ cart: cart.map((i) => [i.product_id, i.variant_id, i.qty]), coupon, extra });
  useEffect(() => {
    if (!cart.length) {
      setQuote(null);
      return;
    }
    const t = setTimeout(() => {
      api.post("/cart/quote", { items: cart.map(({ product_id, variant_id, qty }) => ({ product_id, variant_id, qty })), coupon_code: coupon || null, ...extra })
        .then((r) => setQuote(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return quote;
}

export const SummaryRows = ({ q }) => (
  <div className="space-y-2 text-sm" data-testid="order-summary">
    <Row l="MRP total" v={inr(q.mrp_total)} />
    {q.product_savings > 0 && <Row l="Product discount" v={`− ${inr(q.product_savings)}`} tone="text-[#B91C1C]" />}
    <Row l="Subtotal" v={inr(q.subtotal)} testid="summary-subtotal" />
    {q.coupon_discount > 0 && <Row l={`Coupon (${q.coupon_code})`} v={`− ${inr(q.coupon_discount)}`} tone="text-[#B91C1C]" testid="summary-coupon" />}
    <Row l="Shipping" v={q.shipping ? inr(q.shipping) : "Free"} testid="summary-shipping" />
    <Row l="GST (included in price)" v={inr(q.tax_included)} tone="text-stone-400" />
    <div className="flex justify-between pt-3 border-t border-[#E8E0D5] text-base font-semibold text-[#1B3B2B]">
      <span>Total payable</span><span data-testid="summary-total">{inr(q.total)}</span>
    </div>
  </div>
);
const Row = ({ l, v, tone = "", testid }) => <div className="flex justify-between"><span className="text-stone-500">{l}</span><span className={tone} data-testid={testid}>{v}</span></div>;

export function CouponBox({ error }) {
  const { coupon, setCoupon } = useShop();
  const [code, setCode] = useState(coupon);
  const applied = coupon && !error;
  return (
    <div className="flex gap-2">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input"
        className="flex-1 min-w-0 border border-[#E8E0D5] rounded-full px-4 py-2 text-sm bg-white outline-none focus:border-[#6B8E78]" />
      {applied ? (
        <button onClick={() => { setCoupon(""); setCode(""); }} className="px-4 text-sm rounded-full border border-[#E8E0D5]" data-testid="coupon-remove-button">Remove</button>
      ) : (
        <button onClick={() => setCoupon(code.trim())} className="px-4 text-sm rounded-full bg-[#E2EBE2] text-[#1B3B2B] hover:bg-[#d3e0d3]" data-testid="coupon-apply-button">Apply</button>
      )}
    </div>
  );
}

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem } = useShop();
  const q = useQuote();
  const navigate = useNavigate();
  const left = q && q.free_shipping_threshold ? Math.max(0, q.free_shipping_threshold - (q.subtotal - q.coupon_discount)) : 0;
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#FBF8F3] flex flex-col p-0" data-testid="cart-drawer">
        <div className="p-5 border-b border-[#E8E0D5]">
          <SheetTitle className="font-serif text-2xl text-[#1B3B2B]">Your cart</SheetTitle>
          {q && q.free_shipping_threshold > 0 && (
            <div className="mt-3">
              <p className="text-xs text-stone-600">{left > 0 ? `Add ${inr(left)} more for free shipping` : "You've unlocked free shipping"}</p>
              <div className="h-1.5 bg-[#E8E0D5] rounded-full mt-2"><div className="h-full bg-[#6B8E78] rounded-full transition-[width]" style={{ width: `${Math.min(100, ((q.subtotal - q.coupon_discount) / q.free_shipping_threshold) * 100)}%` }} /></div>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!cart.length && <p className="text-sm text-stone-500" data-testid="cart-empty">Your cart is empty. <Link to="/shop" onClick={() => setCartOpen(false)} className="underline">Start shopping</Link></p>}
          {q?.errors?.map((e) => <p key={e} className="text-xs text-[#B91C1C] bg-red-50 rounded-lg p-2">{e}</p>)}
          {cart.map((i) => {
            const line = q?.lines?.find((l) => l.product_id === i.product_id && (l.variant_id || null) === (i.variant_id || null));
            return (
              <div key={i.key} className="flex gap-3" data-testid={`cart-item-${i.slug}`}>
                <img src={img(i.image)} alt={i.name} className="w-20 h-20 rounded-xl object-cover bg-[#F4EFE6]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">{i.name}</p>
                  {i.variant_name && <p className="text-xs text-stone-500">{i.variant_name}</p>}
                  <p className="text-sm text-[#1B3B2B] mt-1">{inr(line?.price ?? i.price)} {line?.discount_percent > 0 && <span className="text-xs text-[#B91C1C]">({Math.round(line.discount_percent)}% off)</span>}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center border border-[#E8E0D5] rounded-full">
                      <button className="p-1.5" onClick={() => updateQty(i.key, i.qty - 1)} data-testid={`cart-qty-minus-${i.slug}`} aria-label="Decrease"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center text-sm" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
                      <button className="p-1.5" onClick={() => updateQty(i.key, i.qty + 1)} data-testid={`cart-qty-plus-${i.slug}`} aria-label="Increase"><Plus className="w-3 h-3" /></button>
                    </div>
                    <button onClick={() => removeItem(i.key)} className="p-1.5 text-stone-400 hover:text-[#B91C1C]" data-testid={`cart-remove-${i.slug}`} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {cart.length > 0 && q && (
          <div className="p-5 border-t border-[#E8E0D5] space-y-4 bg-[#FFFDF9]">
            <CouponBox error={q.coupon_error} />
            {q.coupon_error && <p className="text-xs text-[#B91C1C]" data-testid="coupon-error">{q.coupon_error}</p>}
            <SummaryRows q={q} />
            <button onClick={() => { setCartOpen(false); navigate("/checkout"); }} data-testid="cart-checkout-button"
              className="w-full bg-[#1B3B2B] text-[#FBF8F3] rounded-full py-3.5 text-sm hover:bg-[#2C523A] transition-colors">Proceed to checkout</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
